import { normalizeMimeType } from "@app/types/files";

export const HTML_ARTIFACT_PREVIEW_SOURCE = "memoair-artifact-preview";

export const HTML_ARTIFACT_PREVIEW_CSP = [
  "default-src 'none'",
  "script-src 'unsafe-inline'",
  "style-src 'unsafe-inline'",
  "img-src data: blob:",
  "font-src data:",
  "connect-src 'none'",
  "form-action 'none'",
  "base-uri 'none'",
  "sandbox allow-scripts",
].join("; ");

export function htmlArtifactPreviewCsp(appUrl: string): string {
  return `${HTML_ARTIFACT_PREVIEW_CSP}; frame-ancestors 'self' ${new URL(appUrl).origin}`;
}

export type HtmlArtifactPreviewTheme = "light" | "dark";

type ArtifactPreviewHostMessage = {
  source: typeof HTML_ARTIFACT_PREVIEW_SOURCE;
  event: "resize";
  height: number;
};

const THEME_TOKENS: Record<HtmlArtifactPreviewTheme, Record<string, string>> = {
  light: {
    "--memoair-background": "#ffffff",
    "--memoair-background-muted": "#f6f6f6",
    "--memoair-border": "#e5e5e5",
    "--memoair-text": "#1b1b1b",
    "--memoair-text-muted": "#727272",
    "--memoair-accent": "#2bb8d6",
  },
  dark: {
    "--memoair-background": "#0d0d0d",
    "--memoair-background-muted": "#1a1a1a",
    "--memoair-border": "#2e2e2e",
    "--memoair-text": "#f2f2f2",
    "--memoair-text-muted": "#a1a1a1",
    "--memoair-accent": "#2bb8d6",
  },
};

/**
 * Whether a file renders in the sandboxed preview frame.
 *
 * Provenance is deliberately not part of this test. The wrapper serves every page under
 * `default-src 'none'` with `sandbox allow-scripts` and no same-origin access, so an
 * uploaded page is no more privileged than an agent-generated artifact, and read
 * authorization is enforced separately by the route. Gating on artifact metadata only
 * meant uploaded HTML fell back to showing its own source.
 */
export function isHtmlPreviewContentType(contentType: string): boolean {
  return normalizeMimeType(contentType).trim() === "text/html";
}

export function isArtifactPreviewHostMessage(
  value: unknown
): value is ArtifactPreviewHostMessage {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  if (!("source" in value) || !("event" in value) || !("height" in value)) {
    return false;
  }
  return (
    value.source === HTML_ARTIFACT_PREVIEW_SOURCE &&
    value.event === "resize" &&
    typeof value.height === "number" &&
    Number.isFinite(value.height)
  );
}

export function jsonForInlineScript(value: unknown): string {
  return JSON.stringify(value)
    .replace(/\u2028/g, "\\u2028")
    .replace(/\u2029/g, "\\u2029")
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/&/g, "\\u0026");
}

function escapeHtmlAttribute(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;");
}

function escapeCssValue(value: string): string {
  return value.replace(/[\\;{}<>]/g, (ch) => {
    if (ch === "\\") {
      return "\\\\";
    }
    return `\\${ch.charCodeAt(0).toString(16)} `;
  });
}

export function renderHtmlArtifactPreview({
  html,
  theme,
}: {
  html: string;
  theme: HtmlArtifactPreviewTheme;
}): string {
  const tokens = THEME_TOKENS[theme];
  const tokenCss = Object.entries(tokens)
    .map(([name, value]) => `  ${name}: ${escapeCssValue(value)};`)
    .join("\n");
  const head = `<meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${escapeHtmlAttribute(HTML_ARTIFACT_PREVIEW_CSP)}"><style>:root {
${tokenCss}
  color-scheme: ${escapeCssValue(theme)};
}
html, body {
  background: var(--memoair-background);
  color: var(--memoair-text);
  margin: 0;
}
</style><script>
(function () {
  function post(event, extra) {
    var message = { source: ${jsonForInlineScript(HTML_ARTIFACT_PREVIEW_SOURCE)}, event: event };
    if (extra) {
      for (var key in extra) {
        if (Object.prototype.hasOwnProperty.call(extra, key)) {
          message[key] = extra[key];
        }
      }
    }
    parent.postMessage(message, "*");
  }
  window.MemoairArtifact = {
    resize: function (height) {
      if (typeof height !== "number" || !isFinite(height)) {
        return;
      }
      post("resize", { height: height });
    }
  };
  function reportHeight() {
    var height = Math.max(
      document.documentElement ? document.documentElement.scrollHeight : 0,
      document.body ? document.body.scrollHeight : 0
    );
    window.MemoairArtifact.resize(height);
  }
  window.addEventListener("load", reportHeight);
  if (window.ResizeObserver && document.documentElement) {
    new ResizeObserver(reportHeight).observe(document.documentElement);
  }
})();
</script>`;

  let document = html;
  if (!/<html[\s>]/i.test(document)) {
    document = `<!DOCTYPE html><html lang="en"><head></head><body>${document}</body></html>`;
  }
  if (!/\sdata-memoair-artifact-preview(\s|>|=)/i.test(document)) {
    document = document.replace(
      /<html(\s|>)/i,
      `<html data-memoair-artifact-preview$1`
    );
  }
  if (/<head[\s>]/i.test(document)) {
    document = document.replace(/<head([^>]*)>/i, `<head$1>${head}`);
  } else {
    document = document.replace(
      /<html([^>]*)>/i,
      `<html$1><head>${head}</head>`
    );
  }
  return document;
}
